import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption, ColumnSelector, useColumnVisibility, type ColumnDefinition } from 'components/ui';
import { EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan, ToolbarAddMenu } from 'components/enterprise-shell';
import DocumentNumberField from 'components/common/DocumentNumberField';
import notificationService from 'services/notificationService';
import { useNavTarget, useNavigation } from 'contexts/NavigationContext';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { usePurchaseNotesRegister } from 'hooks/api/useCommercial';
import {
  BillFormModal, BillViewModal, usePurchaseItemLookups, ItemPickerCell, ItemLineDetails, ItemLineSummary,
  itemFieldsOf, blankItemFields, effectiveItemLedgerId, prefillLedgerLabel, LINE_TYPE_OPTIONS, type ItemLineFields,
} from './PurchaseInvoiceScreen';
import { usePurchaseLineLedgers } from 'hooks/api/useCommercial';
import { purchaseDebitNoteService, purchaseInvoiceService, partyService, voucherTypeService, taxMetaService, entityAccountingConfigService, currencyService, coaLedgerService } from 'api/services';
import SetupGateModal, { useSetupGate } from 'components/modules/setup/SetupGateModal';
import type {
  PurchaseDebitNoteDto, PurchaseNoteRegisterRowDto, PurchaseNoteRegisterQueryParams, PurchaseInvoiceDto, CreatePurchaseDebitNoteRequest, PurchaseDebitNotePrefillDto,
  CreatePurchaseInvoiceTaxRequest, SupplierDuplicateProbe, VoucherTypeDto, PartyDto, TaxComponentDto, InputTaxRulesDto,
  ItcEligibility, PurchaseType, PurchaseLineType, CreatePurchaseDebitNoteLineRequest,
} from 'api/types';
import { FyDateInput } from 'components/common/FyDateInput';
import { useSelectedFinancialYear, defaultDateIn } from 'hooks/useSelectedFinancialYear';

// ADR-0049 Slice 2: the Purchase Debit Note — the mirror-reverse of the bill. EXPENSE lines are
// LEDGER + AMOUNT (each CREDITS its ledger); ITEM lines return goods (ADR-0058 slice 3 — item ×
// pack × qty, locked to the bill line's item when reversing one); tax is CAPTURED as printed on
// the supplier's credit note; the reversal is a tax event of the note's OWN period, never the bill's.
const PURCHASE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'DOMESTIC', label: 'Domestic' },
  { value: 'IMPORT_GOODS', label: 'Import — Goods' },
  { value: 'IMPORT_SERVICES', label: 'Import — Services' },
];
const ELIGIBILITY_OPTIONS: SelectOption[] = [
  { value: 'ELIGIBLE', label: 'Hands back ITC — credits input tax' },
  { value: 'INELIGIBLE', label: 'No ITC was taken — credits cost' },
  { value: 'PARTIAL', label: 'Partly hands back ITC' },
];
const today = () => new Date().toISOString().slice(0, 10);
const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatAddress = (p: PartyDto): string =>
  [p.addressLine1, p.city, p.stateName, p.postalCode, p.countryName].filter(Boolean).join(', ');

const editBlockReason = (r: PurchaseDebitNoteDto): string | null => {
  if (r.docStatus !== 'POSTED') return r.docStatus === 'CANCELLED' ? 'Cancelled' : 'Not posted';
  if (r.isLocked) return r.lockSource || 'Locked (filed or closed period)';
  return null;
};
const statusBadges = (r: PurchaseDebitNoteDto): React.ReactNode => (
  <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap' }}>
    <span className="badge">{r.docStatus}</span>
    {r.docStatus === 'POSTED' && r.onAccount > 0 && <span className="badge" style={{ background: '#2563eb', color: '#fff' }} title="Supplier debit balance — set off against a bill later">On account {money(r.onAccount)}</span>}
    {r.itcEligibility !== 'ELIGIBLE' && <span className="badge" style={{ background: r.itcEligibility === 'INELIGIBLE' ? '#6b7280' : '#d97706', color: '#fff' }}>{r.itcEligibility === 'INELIGIBLE' ? 'No ITC reversed' : 'ITC partly reversed'}</span>}
    {r.isLocked && <span className="badge" style={{ background: '#6b7280', color: '#fff' }} title={r.lockSource || 'Locked'}>🔒 Locked</span>}
  </span>
);

type LineTaxes = Record<string, { taxAmount: string; eligibleAmount: string }>;
interface LineRow extends ItemLineFields { key: number; description: string; expenseLedgerId: string; quantity: string; rate: string; lineAmount: string; sourceInvoiceLineNo: number | null; taxes: LineTaxes; }
const blankLine = (key: number): LineRow => ({ key, description: '', expenseLedgerId: '', quantity: '', rate: '', lineAmount: '', sourceInvoiceLineNo: null, taxes: {}, ...blankItemFields() });
/** A line that goes to the server: an amount, or an item picked (its errors then surface). */
const isSubmitted = (l: LineRow) => parseFloat(l.lineAmount) > 0 || (l.lineType === 'ITEM' && !!l.itemId);
interface TaxRow { componentCode: string; componentName: string; taxAmount: string; eligibleAmount: string; }

// ---- Entry modal ----
interface NoteFormModalProps {
  isOpen: boolean;
  mode?: 'create' | 'edit';
  note?: PurchaseDebitNoteDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, noteNo?: string) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
}
const NoteFormModal: React.FC<NoteFormModalProps> = ({ isOpen, mode = 'create', note, onSaved, onClose, tabId, moduleName, createInstanceId, onRestoreModal, minimizeRef }) => {
  const fy = useSelectedFinancialYear();
  const fyRef = useRef(fy); fyRef.current = fy;
  /** Default posting date: today when it falls in the selected financial year, else the year's last (or first) day. */
  const docToday = () => defaultDateIn(fyRef.current);
  const { minimize } = useModalMinimize();
  const isRestoreRef = useRef(false);
  const noteRef = useRef(note); noteRef.current = note;
  const modeRef = useRef(mode); modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const [editId, setEditId] = useState<string | null>(null);
  const [editRowVersion, setEditRowVersion] = useState(0);
  const [voucherTypeId, setVoucherTypeId] = useState('');
  const [partyId, setPartyId] = useState('');
  const [supplier, setSupplier] = useState<PartyDto | null>(null);
  const [searchAllParties, setSearchAllParties] = useState(false);
  const [againstInvoiceId, setAgainstInvoiceId] = useState('');
  const [prefill, setPrefill] = useState<PurchaseDebitNotePrefillDto | null>(null);
  const [supplierCreditNoteNumber, setSupplierCreditNoteNumber] = useState('');
  const [supplierCreditNoteDate, setSupplierCreditNoteDate] = useState('');
  const [debitNoteDate, setDebitNoteDate] = useState(today());
  const [purchaseType, setPurchaseType] = useState<PurchaseType>('DOMESTIC');
  const [itcEligibility, setItcEligibility] = useState<ItcEligibility>('ELIGIBLE');
  const [lineTaxCapture, setLineTaxCapture] = useState(false);
  const [costLedgerId, setCostLedgerId] = useState('');
  const [currencyCode, setCurrencyCode] = useState('');
  const [exchangeRate, setExchangeRate] = useState('1');
  const [functionalCurrency, setFunctionalCurrency] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const [reason, setReason] = useState('');
  const [narration, setNarration] = useState('');
  const [lines, setLines] = useState<LineRow[]>([blankLine(1)]);
  const lineKey = useRef(1);
  const [taxRows, setTaxRows] = useState<TaxRow[]>([]);
  // ITEM lines — goods returned (ADR-0058 slice 3).
  const itemLookups = usePurchaseItemLookups();
  const loadItemDetailsRef = useRef(itemLookups.loadItemDetails); loadItemDetailsRef.current = itemLookups.loadItemDetails;
  // Where each returned item line credits on the note date (goods ledger fixed; otherwise a pre-fill).
  const lineLedgers = usePurchaseLineLedgers('DEBIT_NOTE', debitNoteDate, lines.filter(l => l.lineType === 'ITEM').map(l => l.itemId), isOpen);

  const [voucherTypes, setVoucherTypes] = useState<VoucherTypeDto[]>([]);
  const [components, setComponents] = useState<TaxComponentDto[]>([]);
  const [taxRules, setTaxRules] = useState<InputTaxRulesDto | null>(null);
  const partyCache = useRef<Map<string, { label: string; currency?: string | null }>>(new Map());
  const billCache = useRef<Map<string, string>>(new Map());
  const currencyLabelCache = useRef<Map<string, string>>(new Map());
  const ledgerCache = useRef<Map<string, string>>(new Map());
  /** Editing a linked note: its own figures, added back to the prefill's "remaining". */
  const ownFiguresRef = useRef<{ grand: number; applied: number; byComponent: Record<string, { tax: number; eligible: number }> }>({ grand: 0, applied: 0, byComponent: {} });

  const [duplicates, setDuplicates] = useState<SupplierDuplicateProbe[]>([]);
  const [loadingBill, setLoadingBill] = useState(false);
  const [descFocusKey, setDescFocusKey] = useState<number | null>(null);
  const [maximized, setMaximized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const initialSnapshotRef = useRef<string>('');

  const linked = !!againstInvoiceId;

  const seedTaxRows = useCallback((comps: TaxComponentDto[], src?: { componentCode: string; taxAmount: number; eligibleAmount?: number | null; lineNo?: number | null }[]): TaxRow[] => {
    const bySrc = new Map((src || []).filter(t => t.lineNo == null).map(t => [t.componentCode.toUpperCase(), t]));
    return comps.map(c => {
      const s = bySrc.get(c.componentCode.toUpperCase());
      return {
        componentCode: c.componentCode,
        componentName: c.componentName,
        taxAmount: s ? String(s.taxAmount) : '',
        eligibleAmount: s && s.eligibleAmount != null ? String(s.eligibleAmount) : '',
      };
    });
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const [vt, comps, rules] = await Promise.all([
        voucherTypeService.getAll({ voucherClass: 'PURCHASE_DEBIT_NOTE', pageSize: 100 }),
        taxMetaService.getComponents().catch(() => [] as TaxComponentDto[]),
        taxMetaService.getInputTaxRules().catch(() => null),
      ]);
      setVoucherTypes(vt.data);
      setComponents(comps);
      setTaxRules(rules);
      setTaxRows(prev => prev.length ? prev : seedTaxRows(comps, noteRef.current?.taxes));
    } catch {
      setVoucherTypes([]); setComponents([]);
    }
  }, [seedTaxRows]);
  useEffect(() => { if (isOpen) loadLookups(); }, [isOpen, loadLookups]);

  const loadPrefill = useCallback(async (billId: string) => {
    if (!billId) { setPrefill(null); return null; }
    try {
      const p = await purchaseDebitNoteService.prefill(billId);
      setPrefill(p);
      return p;
    } catch {
      setPrefill(null);
      notificationService.toast.error('Could not read the bill.');
      return null;
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    lineKey.current = 1;
    setErrors({}); setDuplicates([]); setMaximized(false); setSearchAllParties(false); setPrefill(null);
    ownFiguresRef.current = { grand: 0, applied: 0, byComponent: {} };

    const n = noteRef.current;
    if (modeRef.current === 'edit' && n) {
      const rows: LineRow[] = (n.lines || []).map((l, idx) => {
        if (l.expenseLedgerId) ledgerCache.current.set(l.expenseLedgerId, `${l.expenseLedgerCode || ''} - ${l.expenseLedgerName || ''}`);
        const taxes: LineTaxes = {};
        (n.taxes || []).filter(t => t.lineNo === l.lineNo).forEach(t => {
          taxes[t.componentCode] = { taxAmount: String(t.taxAmount), eligibleAmount: String(t.eligibleAmount) };
        });
        return { key: idx + 1, description: l.description || '', expenseLedgerId: l.expenseLedgerId || '', quantity: l.quantity != null ? String(l.quantity) : '', rate: l.rate != null ? String(l.rate) : '', lineAmount: String(l.lineAmount), sourceInvoiceLineNo: l.sourceInvoiceLineNo ?? null, taxes, ...itemFieldsOf(l) };
      });
      lineKey.current = Math.max(1, rows.length);
      const linesToSet = rows.length ? rows : [blankLine(1)];
      void loadItemDetailsRef.current(rows.map(r => r.itemId));
      setEditId(n.id); setEditRowVersion(n.rowVersion);
      setVoucherTypeId(n.voucherTypeId || '');
      setPartyId(n.partyId); partyCache.current.set(n.partyId, { label: n.partyName || '', currency: n.currencyCode });
      setAgainstInvoiceId(n.againstInvoiceId || '');
      if (n.againstInvoiceId) billCache.current.set(n.againstInvoiceId, `${n.againstInvoiceNumber || ''}${n.againstSupplierInvoiceNumber ? ` · ${n.againstSupplierInvoiceNumber}` : ''}`);
      setSupplierCreditNoteNumber(n.supplierCreditNoteNumber || '');
      setSupplierCreditNoteDate((n.supplierCreditNoteDate || '').slice(0, 10));
      setDebitNoteDate((n.debitNoteDate || '').slice(0, 10));
      setPurchaseType(n.purchaseType || 'DOMESTIC');
      setItcEligibility(n.itcEligibility || 'ELIGIBLE');
      setLineTaxCapture(n.taxCaptureLevel === 'LINE');
      setCostLedgerId(n.taxes?.find(t => t.costLedgerId && t.lineNo == null)?.costLedgerId || '');
      n.taxes?.forEach(t => { if (t.costLedgerId) ledgerCache.current.set(t.costLedgerId, t.costLedgerName || ''); });
      setCurrencyCode(n.currencyCode || ''); setExchangeRate(String(n.exchangeRate ?? 1));
      setManualNumber(n.debitNoteNumber || ''); setReason(n.reason || ''); setNarration(n.narration || '');
      setLines(linesToSet);
      setTaxRows(components.length ? seedTaxRows(components, n.taxes) : []);
      const byComponent: Record<string, { tax: number; eligible: number }> = {};
      (n.taxes || []).forEach(t => {
        const k = t.componentCode.toUpperCase();
        byComponent[k] = { tax: (byComponent[k]?.tax || 0) + t.taxAmount, eligible: (byComponent[k]?.eligible || 0) + t.eligibleAmount };
      });
      ownFiguresRef.current = { grand: n.grandTotal, applied: n.appliedToBill || 0, byComponent };
      if (n.againstInvoiceId) loadPrefill(n.againstInvoiceId);
      initialSnapshotRef.current = JSON.stringify({
        voucherTypeId: n.voucherTypeId || '', partyId: n.partyId, againstInvoiceId: n.againstInvoiceId || '',
        supplierCreditNoteNumber: n.supplierCreditNoteNumber || '', supplierCreditNoteDate: (n.supplierCreditNoteDate || '').slice(0, 10),
        debitNoteDate: (n.debitNoteDate || '').slice(0, 10), purchaseType: n.purchaseType, itcEligibility: n.itcEligibility,
        currencyCode: n.currencyCode || '', exchangeRate: String(n.exchangeRate ?? 1),
        manualNumber: n.debitNoteNumber || '', reason: n.reason || '', narration: n.narration || '', lines: linesToSet,
      });
      entityAccountingConfigService.getConfig().then(c => setFunctionalCurrency(c.functionalCurrencyCode || '')).catch(() => {});
      partyService.getById(n.partyId).then(setSupplier).catch(() => setSupplier(null));
    } else {
      const createLines = [blankLine(1)];
      setEditId(null); setEditRowVersion(0);
      setVoucherTypeId(''); setPartyId(''); setSupplier(null); setAgainstInvoiceId('');
      setSupplierCreditNoteNumber(''); setSupplierCreditNoteDate(''); setDebitNoteDate(docToday());
      setPurchaseType('DOMESTIC'); setItcEligibility('ELIGIBLE'); setLineTaxCapture(false); setCostLedgerId('');
      setCurrencyCode(''); setExchangeRate('1'); setManualNumber(''); setReason(''); setNarration(''); setLines(createLines);
      setTaxRows(components.length ? seedTaxRows(components) : []);
      initialSnapshotRef.current = JSON.stringify({
        voucherTypeId: '', partyId: '', againstInvoiceId: '', supplierCreditNoteNumber: '', supplierCreditNoteDate: '',
        debitNoteDate: docToday(), purchaseType: 'DOMESTIC', itcEligibility: 'ELIGIBLE', currencyCode: '', exchangeRate: '1',
        manualNumber: '', reason: '', narration: '', lines: createLines,
      });
      entityAccountingConfigService.getConfig().then(c => {
        const fc = c.functionalCurrencyCode || '';
        setFunctionalCurrency(fc);
        setCurrencyCode(prev => {
          const next = prev || fc;
          try { const snap = JSON.parse(initialSnapshotRef.current); snap.currencyCode = next; initialSnapshotRef.current = JSON.stringify(snap); } catch { /* restore path */ }
          return next;
        });
      }).catch(() => {});
    }
  }, [isOpen]);

  const selectedVt = voucherTypes.find(v => v.id === voucherTypeId);
  const showManualNumber = !!selectedVt && (selectedVt.numberingMethod === 'MANUAL' || selectedVt.allowManualEntry);

  const loadParties = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const r = await partyService.getAll({ pageSize: 50, searchTerm: input || undefined, partyType: searchAllParties ? undefined : 'SUPPLIER' });
      r.data.forEach(p => partyCache.current.set(p.id, { label: `${p.partyCode} - ${p.partyName}`, currency: p.currencyCode }));
      return r.data.map(p => ({ value: p.id, label: `${p.partyCode} - ${p.partyName}` }));
    } catch { return []; }
  }, [searchAllParties]);
  const loadBills = useCallback(async (input: string): Promise<SelectOption[]> => {
    if (!partyId) return [];
    try {
      // Bills only: a supplier debit note's value is already part of its bill's pool (ADR-0053).
      const r = await purchaseInvoiceService.getAll({ pageSize: 50, partyId, searchTerm: input || undefined, documentKind: 'INVOICE' });
      return r.data.map(b => {
        const label = `${b.invoiceNumber || '—'}${b.supplierInvoiceNumber ? ` · ${b.supplierInvoiceNumber}` : ''} · ${(b.invoiceDate || '').slice(0, 10)} · ${b.currencyCode || ''} ${money(b.grandTotal)}`;
        billCache.current.set(b.id, label);
        return { value: b.id, label };
      });
    } catch { return []; }
  }, [partyId]);
  const loadCurrencies = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const list = await currencyService.getAll(input || undefined);
      return list.map(c => {
        const label = `${c.currencyCode} — ${c.currencyName}`;
        currencyLabelCache.current.set(c.currencyCode, label);
        return { value: c.currencyCode, label };
      });
    } catch { return []; }
  }, []);
  const loadLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined });
      res.data.forEach(l => ledgerCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch { return []; }
  }, []);

  const onPickParty = (id: string) => {
    setPartyId(id);
    setDuplicates([]);
    setAgainstInvoiceId(''); setPrefill(null);
    const cur = partyCache.current.get(id)?.currency || functionalCurrency;
    setCurrencyCode(cur);
    if (cur === functionalCurrency) setExchangeRate('1');
    if (!id) { setSupplier(null); return; }
    partyService.getById(id).then(setSupplier).catch(() => setSupplier(null));
  };

  /** Linking a bill: the note takes its currency, original rate and purchase type, and starts from what remains. */
  const onPickBill = async (billId: string) => {
    setAgainstInvoiceId(billId);
    if (!billId) { setPrefill(null); return; }
    setLoadingBill(true);
    try {
      const [p, bill] = await Promise.all([loadPrefill(billId), purchaseInvoiceService.getById(billId).catch(() => null)]);
      if (!p) return;
      bill?.lines.forEach(l => { if (l.expenseLedgerId) ledgerCache.current.set(l.expenseLedgerId, `${l.expenseLedgerCode || ''} - ${l.expenseLedgerName || ''}`); });
      bill?.taxes.forEach(t => { if (t.costLedgerId) ledgerCache.current.set(t.costLedgerId, t.costLedgerName || ''); });
      setCurrencyCode(p.currencyCode || functionalCurrency);
      setExchangeRate(String(p.exchangeRate ?? 1));
      setPurchaseType(p.purchaseType);
      setItcEligibility(p.itcEligibility);
      const lineMode = p.taxCaptureLevel === 'LINE';
      setLineTaxCapture(lineMode);
      lineKey.current = 0;
      const rows: LineRow[] = p.lines.map(l => {
        const taxes: LineTaxes = {};
        if (lineMode) p.taxes.filter(t => t.lineNo === l.sourceInvoiceLineNo).forEach(t => {
          taxes[t.componentCode] = { taxAmount: String(t.taxAmount), eligibleAmount: t.eligibleAmount != null ? String(t.eligibleAmount) : '' };
        });
        // ITEM lines (goods returnable, quantity = what is not yet returned): labels come from the bill line.
        const billLine = l.sourceInvoiceLineNo != null ? bill?.lines.find(b => b.lineNo === l.sourceInvoiceLineNo) : undefined;
        const item = (l.lineType === 'ITEM' || l.itemId)
          ? itemFieldsOf({
              lineType: 'ITEM', itemId: l.itemId || billLine?.itemId, itemCode: billLine?.itemCode, itemName: billLine?.itemName,
              uomId: l.uomId || billLine?.uomId, uomCode: billLine?.uomCode, factorToBase: billLine?.factorToBase,
              stockLocationId: l.stockLocationId || billLine?.stockLocationId, locationCode: billLine?.locationCode, locationName: billLine?.locationName,
            })
          : blankItemFields();
        return {
          key: ++lineKey.current, description: l.description || '', expenseLedgerId: l.expenseLedgerId || '',
          quantity: l.quantity != null ? String(l.quantity) : '', rate: l.rate != null ? String(l.rate) : '',
          lineAmount: String(l.lineAmount), sourceInvoiceLineNo: l.sourceInvoiceLineNo ?? null, taxes, ...item,
        };
      });
      void itemLookups.loadItemDetails(rows.map(r => r.itemId));
      setLines(rows.length ? rows : [blankLine(++lineKey.current)]);
      setTaxRows(seedTaxRows(components, lineMode ? [] : p.taxes));
      const firstCost = p.taxes.find(t => t.costLedgerId)?.costLedgerId;
      setCostLedgerId(firstCost || '');
    } finally { setLoadingBill(false); }
  };

  const checkDuplicates = useCallback(async () => {
    const num = supplierCreditNoteNumber.trim();
    if (!partyId || !num) { setDuplicates([]); return; }
    try { setDuplicates(await purchaseDebitNoteService.duplicateCheck(partyId, num, editId || undefined)); }
    catch { setDuplicates([]); }
  }, [partyId, supplierCreditNoteNumber, editId]);

  const updLine = (key: number, patch: Partial<LineRow>) => setLines(rows => rows.map(r => {
    if (r.key !== key) return r;
    const n = { ...r, ...patch };
    if (patch.quantity !== undefined || patch.rate !== undefined) {
      const q = parseFloat(n.quantity), rt = parseFloat(n.rate);
      if (!isNaN(q) && !isNaN(rt)) n.lineAmount = (q * rt).toFixed(2);
    }
    return n;
  }));
  const addLine = () => setLines(rows => [...rows, blankLine(++lineKey.current)]);
  const removeLine = (key: number) => setLines(rows => rows.length > 1 ? rows.filter(r => r.key !== key) : rows);
  /** Expense ↔ Item: an expense line drops the item. */
  const setLineType = (key: number, lineType: PurchaseLineType) => setLines(rows => rows.map(r => {
    if (r.key !== key || r.lineType === lineType) return r;
    return lineType === 'ITEM' ? { ...r, lineType: 'ITEM' } : { ...r, ...blankItemFields() };
  }));
  /** A returned bill ITEM line keeps the bill's item and pack (the server refuses another item). */
  const isLockedItem = (l: LineRow) => linked && l.sourceInvoiceLineNo != null && l.lineType === 'ITEM' && !!l.itemId;
  const updTax = (code: string, patch: Partial<TaxRow>) =>
    setTaxRows(rows => rows.map(r => r.componentCode === code ? { ...r, ...patch } : r));
  const updLineTax = (key: number, code: string, patch: Partial<{ taxAmount: string; eligibleAmount: string }>) =>
    setLines(rows => rows.map(r => {
      if (r.key !== key) return r;
      const existing = r.taxes[code] ?? { taxAmount: '', eligibleAmount: '' };
      return { ...r, taxes: { ...r.taxes, [code]: { ...existing, ...patch } } };
    }));

  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.lineAmount) || 0), 0);
  const taxByComponent: Record<string, { tax: number; eligible: number }> = {};
  const addTax = (code: string, t: number, e: number) => {
    const k = code.toUpperCase();
    if (!taxByComponent[k]) taxByComponent[k] = { tax: 0, eligible: 0 };
    taxByComponent[k].tax += t; taxByComponent[k].eligible += e;
  };
  if (lineTaxCapture) {
    lines.forEach(l => Object.entries(l.taxes).forEach(([code, v]) => {
      const t = parseFloat(v.taxAmount) || 0;
      const e = itcEligibility === 'ELIGIBLE' ? t : itcEligibility === 'INELIGIBLE' ? 0 : Math.min(parseFloat(v.eligibleAmount) || 0, t);
      addTax(code, t, e);
    }));
  } else {
    taxRows.forEach(r => {
      const t = parseFloat(r.taxAmount) || 0;
      const e = itcEligibility === 'ELIGIBLE' ? t : itcEligibility === 'INELIGIBLE' ? 0 : Math.min(parseFloat(r.eligibleAmount) || 0, t);
      addTax(r.componentCode, t, e);
    });
  }
  const taxTotal = Object.values(taxByComponent).reduce((s, v) => s + v.tax, 0);
  const eligibleTotal = Object.values(taxByComponent).reduce((s, v) => s + v.eligible, 0);
  const toCost = taxTotal - eligibleTotal;
  const grandTotal = subtotal + taxTotal;

  // Caps against the linked bill (the server enforces the same — this is the early warning).
  const own = ownFiguresRef.current;
  const remainingDebitable = prefill ? prefill.remainingDebitable + (editId ? own.grand : 0) : null;
  // Settlement preview: the note settles the bill's UNPAID balance first (an edit releases
  // what the note itself had settled), the rest sits on account — the same split the server makes.
  const openForNote = prefill ? prefill.openBalance + (editId ? own.applied : 0) : 0;
  const toBill = linked && prefill ? Math.min(grandTotal, openForNote) : 0;
  const toAccount = Math.max(0, grandTotal - toBill);
  const settlementSentence = !linked
    ? `${currencyCode} ${money(grandTotal)} on account (no bill linked).`
    : `Settles ${money(toBill)} against bill ${prefill?.invoiceNumber || ''}${toAccount > 0.005 ? ` · ${money(toAccount)} on account${openForNote <= 0.005 ? ' (the bill is fully paid)' : ' (beyond the bill\'s unpaid balance)'}` : ''}.`;
  const capWarnings: string[] = [];
  if (prefill && remainingDebitable != null) {
    if (grandTotal > remainingDebitable + 0.005) capWarnings.push(`Exceeds what remains on bill ${prefill.invoiceNumber}: ${money(remainingDebitable)}.`);
    Object.entries(taxByComponent).forEach(([code, v]) => {
      const c = prefill.components.find(x => x.componentCode.toUpperCase() === code);
      const mine = editId ? own.byComponent[code] : undefined;
      if (!c) { if (v.tax > 0) capWarnings.push(`The bill carries no ${code}.`); return; }
      const taxLeft = c.remaining + (mine?.tax || 0);
      const eligibleLeft = c.remainingEligible + (mine?.eligible || 0);
      if (v.tax > taxLeft + 0.005) capWarnings.push(`${code}: only ${money(taxLeft)} left to reverse on the bill.`);
      else if (v.eligible > eligibleLeft + 0.005) capWarnings.push(`${code}: the bill claimed only ${money(eligibleLeft)} of ITC not yet handed back — mark the rest as cost.`);
    });
  }

  const setOf = (code: string) =>
    taxRules?.componentSets.find(s => s.componentCodes.some(c => c.toUpperCase() === code.toUpperCase())) ?? null;
  const usedSetCodes = new Set<string>();
  if (taxRules && taxRules.exclusivityScope === 'DOCUMENT' && taxRules.componentSets.length > 0) {
    Object.entries(taxByComponent).filter(([, v]) => v.tax > 0).forEach(([c]) => { const s = setOf(c); if (s) usedSetCodes.add(s.setCode); });
  }
  const componentDisabled = (code: string): string | null => {
    if (usedSetCodes.size !== 1) return null;
    const s = setOf(code);
    if (!s || usedSetCodes.has(s.setCode)) return null;
    const active = taxRules!.componentSets.find(x => usedSetCodes.has(x.setCode));
    return `The note already carries ${active?.setName || 'another treatment'} — one treatment per document.`;
  };
  const mixedSetNames = usedSetCodes.size > 1
    ? taxRules!.componentSets.filter(s => usedSetCodes.has(s.setCode)).map(s => s.setName).join(' vs ')
    : null;

  const currentSnapshot = useMemo(() => JSON.stringify({
    voucherTypeId, partyId, againstInvoiceId, supplierCreditNoteNumber, supplierCreditNoteDate, debitNoteDate, purchaseType, itcEligibility,
    lineTaxCapture, costLedgerId, currencyCode, exchangeRate, manualNumber, reason, narration, lines, taxRows,
  }), [voucherTypeId, partyId, againstInvoiceId, supplierCreditNoteNumber, supplierCreditNoteDate, debitNoteDate, purchaseType, itcEligibility, lineTaxCapture, costLedgerId, currencyCode, exchangeRate, manualNumber, reason, narration, lines, taxRows]);
  const isDirty = useMemo(() => !!initialSnapshotRef.current && currentSnapshot !== initialSnapshotRef.current, [currentSnapshot]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleSave = () => {
    const e: Record<string, string> = {};
    if (!voucherTypeId) e.voucherTypeId = voucherTypes.length === 0
      ? 'No voucher type of class Purchase Debit Note — add one under Accounts → Voucher Types'
      : 'Voucher type is required';
    if (!partyId) e.partyId = 'Supplier is required';
    if (showManualNumber && selectedVt?.numberingMethod === 'MANUAL' && !manualNumber.trim()) e.manualNumber = 'Number is required';
    if (!debitNoteDate) e.debitNoteDate = 'Date is required';
    if (supplierCreditNoteDate && debitNoteDate && debitNoteDate < supplierCreditNoteDate) e.debitNoteDate = "Cannot be before the supplier's credit note date";
    if (prefill && debitNoteDate && debitNoteDate < (prefill.invoiceDate || '').slice(0, 10)) e.debitNoteDate = `Cannot be before the bill's date (${(prefill.invoiceDate || '').slice(0, 10)})`;
    if (!linked) {
      if (!currencyCode) e.currencyCode = 'Currency is required';
      if (currencyCode && currencyCode !== functionalCurrency && !(parseFloat(exchangeRate) > 0)) e.exchangeRate = `Enter the rate — 1 ${currencyCode} in ${functionalCurrency}`;
    }
    const valid = lines.filter(isSubmitted);
    if (valid.length === 0) e.lines = 'Add at least one line with an amount';
    // ITEM lines (goods returned) — the server's rules, caught early; it also caps the quantity at the bill line's.
    const lineErrors: string[] = [];
    valid.forEach((l, idx) => {
      if (l.lineType !== 'ITEM') return;
      const n = idx + 1;
      if (!l.itemId) { e[`line-${l.key}-item`] = 'Required'; lineErrors.push(`Line ${n}: pick the item`); return; }
      if (!(parseFloat(l.quantity) > 0)) { e[`line-${l.key}-quantity`] = 'Required'; lineErrors.push(`Line ${n}: enter the quantity returned (more than 0)`); }
      if (itemLookups.itemTypeOf(l) === 'STOCK' && !l.stockLocationId) { e[`line-${l.key}-location`] = 'Required'; lineErrors.push(`Line ${n}: pick the location the goods leave from`); }
    });
    if (lineErrors.length && !e.lines) e.lines = lineErrors[0];
    if (mixedSetNames && taxRules?.exclusivitySeverity === 'BLOCK') e.taxes = `These tax components cannot be mixed on one document: ${mixedSetNames}.`;
    if (itcEligibility === 'PARTIAL' && taxTotal <= 0) e.taxes = 'Partly hands back ITC — but no tax is entered';
    if (capWarnings.length) e.caps = capWarnings[0];
    // Per-field line flags (line-<key>-…) only highlight; the toast reads a sentence.
    if (Object.keys(e).length) { setErrors(e); notificationService.toast.warning(Object.entries(e).find(([k]) => !k.startsWith('line-'))?.[1] || Object.values(e)[0]); return; }
    setErrors({}); setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmOpen(false); setSaving(true);
    try {
      const submitLines = lines.filter(isSubmitted);
      const toLineRequest = (l: LineRow): CreatePurchaseDebitNoteLineRequest => {
        // ITEM lines: a goods ledger is fixed (no ledger sent); otherwise the line's own ledger
        // (picked, or kept from the bill line it reverses), else the pre-fill.
        const ledgerId = l.lineType === 'ITEM' ? effectiveItemLedgerId(l.expenseLedgerId, l.itemId ? lineLedgers[l.itemId] : undefined) : l.expenseLedgerId;
        const common = {
          expenseLedgerId: ledgerId || undefined,
          quantity: l.quantity ? parseFloat(l.quantity) : undefined, rate: l.rate ? parseFloat(l.rate) : undefined,
          lineAmount: parseFloat(l.lineAmount) || 0,
          sourceInvoiceLineNo: linked ? l.sourceInvoiceLineNo : undefined,
        };
        if (l.lineType === 'ITEM') {
          return {
            lineType: 'ITEM', ...common,
            // Blank description → the item's name.
            description: l.description.trim() || l.itemName || undefined,
            itemId: l.itemId, uomId: l.uomId || undefined,
            stockLocationId: itemLookups.itemTypeOf(l) === 'NON_STOCK' ? undefined : (l.stockLocationId || undefined),
          };
        }
        return { lineType: 'EXPENSE', ...common, description: l.description.trim() || undefined };
      };
      const taxes: CreatePurchaseInvoiceTaxRequest[] = lineTaxCapture
        ? submitLines.flatMap((l, idx) => Object.entries(l.taxes)
            .filter(([, v]) => (parseFloat(v.taxAmount) || 0) > 0)
            .map(([code, v]) => ({
              lineNo: idx + 1,
              componentCode: code,
              taxAmount: parseFloat(v.taxAmount) || 0,
              eligibleAmount: itcEligibility === 'PARTIAL' ? (parseFloat(v.eligibleAmount) || 0) : undefined,
            })))
        : taxRows.filter(t => parseFloat(t.taxAmount) > 0).map(t => ({
            componentCode: t.componentCode,
            taxAmount: parseFloat(t.taxAmount) || 0,
            eligibleAmount: itcEligibility === 'PARTIAL' ? (parseFloat(t.eligibleAmount) || 0) : undefined,
            costLedgerId: itcEligibility !== 'ELIGIBLE' ? (costLedgerId || undefined) : undefined,
          }));
      const payload: CreatePurchaseDebitNoteRequest = {
        voucherTypeId, partyId,
        againstInvoiceId: againstInvoiceId || undefined,
        supplierCreditNoteNumber: supplierCreditNoteNumber.trim() || undefined,
        supplierCreditNoteDate: supplierCreditNoteDate || undefined,
        debitNoteDate,
        purchaseType, itcEligibility,
        taxCaptureLevel: lineTaxCapture ? 'LINE' : 'INVOICE',
        currencyCode: linked ? undefined : currencyCode,
        exchangeRate: linked ? undefined : (parseFloat(exchangeRate) || 1),
        manualNumber: (selectedVt?.numberingMethod === 'MANUAL' && manualNumber.trim()) ? manualNumber.trim() : undefined,
        reason: reason.trim() || undefined,
        narration: narration.trim() || undefined,
        lines: submitLines.map(toLineRequest),
        taxes,
      };
      const saved = editId
        ? await purchaseDebitNoteService.update(editId, { ...payload, rowVersion: editRowVersion })
        : await purchaseDebitNoteService.create(payload);
      setSaving(false);
      const tail = saved.onAccount > 0 ? ` ${saved.currencyCode || ''} ${money(saved.onAccount)} sits on account.` : '';
      notificationService.toast.success(`Debit note ${saved.debitNoteNumber || ''} ${editId ? 'updated' : 'posted'}.${tail}`);
      onSaved(); onClose();
    } catch (err: any) { setSaving(false); notificationService.toast.error(err?.response?.data?.message || `Could not ${editId ? 'update' : 'post'} the debit note.`); }
  };

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const editing = modeRef.current === 'edit';
    const cId = createInstanceIdRef.current || '1';
    const snap = { editId, editRowVersion, voucherTypeId, partyId, supplier, againstInvoiceId, prefill, supplierCreditNoteNumber, supplierCreditNoteDate, debitNoteDate, purchaseType, itcEligibility, lineTaxCapture, costLedgerId, currencyCode, exchangeRate, functionalCurrency, manualNumber, reason, narration, lines: lines.map(l => ({ ...l, taxes: { ...l.taxes } })), taxRows: taxRows.map(t => ({ ...t })), own: ownFiguresRef.current };
    const savedInitialSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedInitialSnapshot ? currentSnapshot !== savedInitialSnapshot : false;
    minimize({
      id: editing ? `${tabId}-form-modal-edit-${manualNumber || editId || ''}` : `${tabId}-form-modal-create-${cId}`,
      title: editing ? `Edit Debit Note - ${manualNumber || ''}` : partyId ? `Debit Note - ${partyCache.current.get(partyId)?.label || ''} (${cId})` : `New Debit Note (${cId})`,
      moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setEditId(snap.editId); setEditRowVersion(snap.editRowVersion);
        setVoucherTypeId(snap.voucherTypeId); setPartyId(snap.partyId); setSupplier(snap.supplier);
        setAgainstInvoiceId(snap.againstInvoiceId); setPrefill(snap.prefill);
        setSupplierCreditNoteNumber(snap.supplierCreditNoteNumber); setSupplierCreditNoteDate(snap.supplierCreditNoteDate); setDebitNoteDate(snap.debitNoteDate);
        setPurchaseType(snap.purchaseType); setItcEligibility(snap.itcEligibility); setLineTaxCapture(snap.lineTaxCapture); setCostLedgerId(snap.costLedgerId);
        setCurrencyCode(snap.currencyCode); setExchangeRate(snap.exchangeRate); setFunctionalCurrency(snap.functionalCurrency);
        setManualNumber(snap.manualNumber); setReason(snap.reason); setNarration(snap.narration); setLines(snap.lines); setTaxRows(snap.taxRows);
        void loadItemDetailsRef.current(snap.lines.map(l => l.itemId));
        ownFiguresRef.current = snap.own;
        initialSnapshotRef.current = savedInitialSnapshot;
        loadLookups();
        onRestoreModal?.(editing ? 'edit' : 'create', editing ? undefined : cId, editing ? (snap.manualNumber || undefined) : undefined);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, editId, editRowVersion, voucherTypeId, partyId, supplier, againstInvoiceId, prefill, supplierCreditNoteNumber, supplierCreditNoteDate, debitNoteDate, purchaseType, itcEligibility, lineTaxCapture, costLedgerId, currencyCode, exchangeRate, functionalCurrency, manualNumber, reason, narration, lines, taxRows, currentSnapshot, loadLookups]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  if (!isOpen) return null;
  const tinyNote = { fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.15rem' } as const;
  return (
    <>
      <div className="modal-backdrop" />
      <div className={`modal-container ${maximized ? '' : 'modal-xl'}`} style={maximized ? { width: '98vw', maxWidth: '98vw', height: '96vh', maxHeight: '96vh' } : undefined}>
        {(saving || loadingBill) && <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}><div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>}
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">{editId ? `Edit Debit Note ${manualNumber || ''}` : 'New Purchase Debit Note'}</h2><div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}><button className="modal-header-btn" onClick={() => setMaximized(m => !m)} title={maximized ? 'Restore' : 'Maximize'} disabled={saving}>{maximized ? <IconSystem.windowRestore size={16} /> : <IconSystem.maximize size={16} />}</button>{tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}><IconSystem.chevronDown size={16} /></button>}<button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button></div></div>
        <div className="modal-content">
          <div className="form-grid-3">
            <div className="form-group"><SyutSelect label={<>Voucher Type <span className="text-danger">*</span></>} options={voucherTypes.map(v => ({ value: v.id, label: `${v.voucherCode} - ${v.voucherName}` }))} value={voucherTypeId} onChange={v => setVoucherTypeId((v as string) || '')} isClearable={false} isDisabled={!!editId} menuPortalTarget={document.body} error={errors.voucherTypeId} placeholder={voucherTypes.length === 0 ? 'None of class Purchase Debit Note' : 'Select'} /></div>
            <div className="form-group">
              <SyutSelect key={String(searchAllParties)} label={<>Supplier <span className="text-danger">*</span></>} isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                seedOptions={partyId ? [{ value: partyId, label: partyCache.current.get(partyId)?.label || '' }] : undefined}
                menuPortalTarget={document.body} value={partyId} onChange={v => onPickParty((v as string) || '')} placeholder={searchAllParties ? 'Search all parties' : 'Select supplier'} error={errors.partyId} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={searchAllParties} onChange={e => setSearchAllParties(e.target.checked)} />
                Supplier not listed? Search all parties
              </label>
            </div>
            <div className="form-group">
              <SyutSelect key={partyId || 'no-party'} label="Against Bill" isAsync loadOptions={loadBills} defaultOptions cacheOptions={false}
                seedOptions={againstInvoiceId ? [{ value: againstInvoiceId, label: billCache.current.get(againstInvoiceId) || '' }] : undefined}
                menuPortalTarget={document.body} value={againstInvoiceId} onChange={v => onPickBill((v as string) || '')} isClearable
                isDisabled={!partyId} placeholder={partyId ? 'Optional — none puts it on account' : 'Pick the supplier first'} />
              <div style={tinyNote}>{linked ? 'Settles the bill; any excess stays on account' : 'Unlinked: the whole note sits on account, to set off against a bill later'}</div>
            </div>
            <DocumentNumberField label="Our No." voucherTypeId={voucherTypeId} date={debitNoteDate}
              existingNumber={editId ? manualNumber : ''} manualValue={manualNumber} onManualChange={setManualNumber}
              required error={errors.manualNumber} />
            <div className="form-group">
              <label className="form-label">Supplier Credit Note No.</label>
              <input className="form-input" value={supplierCreditNoteNumber} maxLength={50}
                onChange={e => setSupplierCreditNoteNumber(e.target.value)} onBlur={checkDuplicates} placeholder="The supplier's own number" />
              {duplicates.length > 0 && (
                <div style={{ fontSize: '0.72rem', color: '#d97706', marginTop: '0.2rem' }}>
                  ⚠ Already recorded for this supplier: {duplicates.slice(0, 3).map(d => `${d.invoiceNumber} (${(d.invoiceDate || '').slice(0, 10)}, ${money(d.grandTotal)})`).join('; ')}. Posting is still allowed.
                </div>
              )}
            </div>
            <div className="form-group"><label className="form-label">Supplier Credit Note Date</label><input type="date" className="form-input" value={supplierCreditNoteDate} onChange={e => setSupplierCreditNoteDate(e.target.value)} /><div style={tinyNote}>The tax date — the period the reversal belongs to</div></div>
            <div className="form-group"><label className="form-label">Debit Note Date <span className="text-danger">*</span></label><FyDateInput className={`form-input${errors.debitNoteDate ? ' form-field-error' : ''}`} value={debitNoteDate} onChange={setDebitNoteDate} />{errors.debitNoteDate ? <div className="form-error-message">{errors.debitNoteDate}</div> : <div style={tinyNote}>Drives the posting period — never the bill's</div>}</div>
            <div className="form-group"><SyutSelect label="Purchase Type" options={PURCHASE_TYPE_OPTIONS} value={purchaseType} onChange={v => setPurchaseType(((v as string) || 'DOMESTIC') as PurchaseType)} isClearable={false} isDisabled={linked} menuPortalTarget={document.body} /></div>
            <div className="form-group">
              <SyutSelect label="Currency" isAsync defaultOptions cacheOptions={false} loadOptions={loadCurrencies}
                seedOptions={currencyCode ? [{ value: currencyCode, label: currencyLabelCache.current.get(currencyCode) || currencyCode }] : undefined}
                value={currencyCode} onChange={v => { const c = (v as string) || ''; setCurrencyCode(c); setExchangeRate(c === functionalCurrency ? '1' : ''); }}
                isDisabled={linked} menuPortalTarget={document.body} isClearable={false} error={errors.currencyCode} />
              {linked && <div style={tinyNote}>The bill's currency</div>}
            </div>
            {currencyCode && currencyCode !== functionalCurrency && (
              <div className="form-group">
                <label className="form-label">Exchange Rate <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>→ {functionalCurrency}</span></label>
                <input type="number" step="0.000001" min="0" className={`form-input${errors.exchangeRate ? ' form-field-error' : ''}`}
                  value={exchangeRate} disabled={linked} onChange={e => setExchangeRate(e.target.value)} placeholder={`1 ${currencyCode} = ? ${functionalCurrency}`} />
                {errors.exchangeRate ? <div className="form-error-message">{errors.exchangeRate}</div> : linked && <div style={tinyNote}>The bill's original rate — a clean reversal, no exchange difference</div>}
              </div>
            )}
            <div className="form-group"><label className="form-label">Reason</label><input className="form-input" value={reason} maxLength={200} onChange={e => setReason(e.target.value)} placeholder="Return, rate difference, discount…" /></div>
          </div>

          {supplier && (
            <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', fontSize: '0.8rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1.5rem' }}>
              <span style={{ fontWeight: 600 }}>Supplier: {supplier.partyName}</span>
              {formatAddress(supplier) && <span style={{ color: 'var(--text-secondary)' }}>{formatAddress(supplier)}</span>}
            </div>
          )}
          {prefill && (
            // Two questions, two rows (user review 2026-10-03): how much MAY this bill still be
            // debited (the cap) vs how much of it is UNPAID right now (what the note can settle).
            <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid var(--border-color)', fontSize: '0.8rem', display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '0.3rem 1rem', alignItems: 'baseline' }}>
              <span style={{ fontWeight: 600 }}>Bill {prefill.invoiceNumber}{prefill.supplierInvoiceNumber ? ` (${prefill.supplierInvoiceNumber})` : ''}</span>
              <span>{prefill.currencyCode} {money(prefill.invoiceGrandTotal)}{functionalCurrency && prefill.currencyCode && prefill.currencyCode !== functionalCurrency && <span style={{ color: 'var(--text-muted)' }}> · {functionalCurrency} {money(prefill.invoiceGrandTotal * prefill.exchangeRate)} @ {prefill.exchangeRate}</span>}</span>

              <span style={{ color: 'var(--text-secondary)' }}>Debit-note limit</span>
              <span>
                <strong>{money(remainingDebitable ?? 0)}</strong> can still be debited
                <span style={{ color: 'var(--text-muted)' }}> (bill total{prefill.supplierDebitNoteTotal ? ` + supplier debit notes ${money(prefill.supplierDebitNoteTotal)}` : ''} less earlier notes {money(prefill.alreadyDebited - (editId ? own.grand : 0))})</span>
                {prefill.components.map(c => (
                  <span key={c.componentCode} style={{ color: 'var(--text-muted)' }}>
                    {' · '}{c.componentCode} {money(c.remaining + (editId ? (own.byComponent[c.componentCode.toUpperCase()]?.tax || 0) : 0))} reversible
                    {' '}(ITC {money(c.remainingEligible + (editId ? (own.byComponent[c.componentCode.toUpperCase()]?.eligible || 0) : 0))})
                  </span>
                ))}
              </span>

              <span style={{ color: 'var(--text-secondary)' }}>Settlement</span>
              <span>
                <strong>{money(openForNote)}</strong> unpaid — this note settles up to that; any excess goes on account
                {openForNote <= 0.005 && <span className="badge" style={{ background: '#16a34a', color: '#fff', marginLeft: '0.35rem' }}>Fully paid — the note goes on account</span>}
              </span>
            </div>
          )}

          {/* Lines — EXPENSE: ledger + amount, each CREDITS its ledger; ITEM: goods returned (ADR-0058 slice 3) */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span className="form-label" style={{ margin: 0 }}>Lines</span>
              <button className="btn btn-secondary" onClick={addLine} style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}><IconSystem.add size={12} /> Add line</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '0.3rem', width: 110 }}>Type</th>
                <th style={{ padding: '0.3rem', width: 260 }}>Ledger (credited) / Item</th>
                <th style={{ padding: '0.3rem', width: 80 }}>Qty</th><th style={{ padding: '0.3rem', width: 100 }}>Rate</th>
                <th style={{ padding: '0.3rem', width: 130, textAlign: 'right' }}>Amount</th><th style={{ width: 30 }} />
              </tr></thead>
              <tbody>
                {lines.map(l => {
                  const isItem = l.lineType === 'ITEM';
                  const locked = isLockedItem(l);
                  const ledgerInfo = isItem && l.itemId ? lineLedgers[l.itemId] : undefined;
                  // ITEM lines: the line's own ledger (a reversed bill line keeps the bill's), else the pre-fill.
                  const shownLedgerId = isItem ? effectiveItemLedgerId(l.expenseLedgerId, ledgerInfo) : l.expenseLedgerId;
                  const ledgerSelect = (placeholder: string) => (
                    <SyutSelect isAsync loadOptions={loadLedgers} defaultOptions cacheOptions={false}
                      seedOptions={shownLedgerId ? [{ value: shownLedgerId, label: ledgerCache.current.get(shownLedgerId) || (shownLedgerId === ledgerInfo?.ledgerId ? prefillLedgerLabel(ledgerInfo) : '') }] : undefined}
                      value={shownLedgerId} onChange={v => updLine(l.key, { expenseLedgerId: (v as string) || '' })} isClearable size="sm" menuPortalTarget={document.body} placeholder={placeholder} />
                  );
                  return (
                  <React.Fragment key={l.key}>
                    <tr style={{ borderTop: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.25rem' }}>
                        <SyutSelect options={LINE_TYPE_OPTIONS} value={l.lineType} onChange={v => setLineType(l.key, ((v as string) || 'EXPENSE') as PurchaseLineType)}
                          isClearable={false} isDisabled={locked} size="sm" menuPortalTarget={document.body} />
                      </td>
                      <td style={{ padding: '0.25rem' }}>
                        {isItem
                          ? <ItemPickerCell row={l} lookups={itemLookups} locked={locked} onPick={id => { void itemLookups.pickItem(l.key, id, setLines); }} error={errors[`line-${l.key}-item`]} />
                          : ledgerSelect('Expense ledger (or default)')}
                        {linked && l.sourceInvoiceLineNo != null && <div style={tinyNote}>{isItem ? 'Returns' : 'Reverses'} bill line {l.sourceInvoiceLineNo}</div>}
                      </td>
                      <td style={{ padding: '0.25rem' }}><input className={`form-input${errors[`line-${l.key}-quantity`] ? ' form-field-error' : ''}`} style={{ fontSize: '0.8125rem' }} type="number" value={l.quantity} title={isItem && l.uomCode ? `In ${l.uomCode}` : undefined} placeholder={isItem ? 'Qty *' : undefined} onChange={e => updLine(l.key, { quantity: e.target.value })} /></td>
                      <td style={{ padding: '0.25rem' }}><input className="form-input" style={{ fontSize: '0.8125rem' }} type="number" value={l.rate} title={isItem && l.uomCode ? `Per ${l.uomCode}` : undefined} onChange={e => updLine(l.key, { rate: e.target.value })} /></td>
                      <td style={{ padding: '0.25rem' }}><input className="form-input" style={{ fontSize: '0.8125rem', textAlign: 'right' }} type="number" value={l.lineAmount} onChange={e => updLine(l.key, { lineAmount: e.target.value })} /></td>
                      <td style={{ textAlign: 'center' }}><button title="Remove" onClick={() => removeLine(l.key)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem' }}><IconSystem.x size={14} /></button></td>
                    </tr>
                    {isItem && (
                      <tr>
                        <td colSpan={6} style={{ padding: '0 0.25rem 0.4rem 0.5rem' }}>
                          <ItemLineDetails row={l} lookups={itemLookups} onChange={p => updLine(l.key, p)} packLocked={locked}
                            ledger={ledgerSelect(linked && l.sourceInvoiceLineNo != null ? "The bill line's ledger" : 'Purchase default')}
                            ledgerInfo={ledgerInfo} pickedLedgerId={l.expenseLedgerId}
                            errors={{ uom: errors[`line-${l.key}-uom`], location: errors[`line-${l.key}-location`] }} />
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={6} style={{ padding: '0 0.25rem 0.5rem 0.5rem' }}>
                        <textarea className="form-textarea" rows={descFocusKey === l.key ? 3 : 1}
                          style={{ fontSize: '0.8rem', minHeight: descFocusKey === l.key ? '3.6rem' : '2rem', width: '100%', transition: 'min-height 0.12s ease' }}
                          value={l.description} placeholder={isItem ? 'Description… (blank = the item name)' : 'Description…'}
                          onFocus={() => setDescFocusKey(l.key)} onBlur={() => setDescFocusKey(null)}
                          onChange={e => updLine(l.key, { description: e.target.value })} />
                      </td>
                    </tr>
                    {lineTaxCapture && components.length > 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: '0 0.25rem 0.6rem 0.5rem' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.75rem', alignItems: 'end', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.4rem 0.6rem' }}>
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', alignSelf: 'center' }}>Tax reversed on this line:</span>
                            {components.map(c => {
                              const v = l.taxes[c.componentCode] ?? { taxAmount: '', eligibleAmount: '' };
                              return (
                                <React.Fragment key={c.componentCode}>
                                  <div>
                                    <label className="form-label" style={{ fontSize: '0.68rem', marginBottom: '0.1rem' }}>{c.componentCode}</label>
                                    <input className="form-input" style={{ width: 110, textAlign: 'right', fontSize: '0.8rem', opacity: componentDisabled(c.componentCode) ? 0.5 : 1 }} type="number" placeholder="0.00"
                                      disabled={!!componentDisabled(c.componentCode)} title={componentDisabled(c.componentCode) || undefined}
                                      value={v.taxAmount} onChange={e => updLineTax(l.key, c.componentCode, { taxAmount: e.target.value })} />
                                  </div>
                                  {itcEligibility === 'PARTIAL' && parseFloat(v.taxAmount) > 0 && (
                                    <div>
                                      <label className="form-label" style={{ fontSize: '0.68rem', marginBottom: '0.1rem' }}>ITC part</label>
                                      <input className="form-input" style={{ width: 110, textAlign: 'right', fontSize: '0.8rem' }} type="number" placeholder="0.00"
                                        value={v.eligibleAmount} onChange={e => updLineTax(l.key, c.componentCode, { eligibleAmount: e.target.value })} />
                                    </div>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {errors.lines && <div className="form-error-message">{errors.lines}</div>}
          </div>

          {/* Tax reversed — as printed on the supplier's credit note */}
          {components.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  Tax on the supplier's credit note
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.72rem', fontWeight: 400, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={lineTaxCapture} onChange={e => setLineTaxCapture(e.target.checked)} />
                    Capture tax per line
                  </label>
                </span>
                <div style={{ minWidth: 320 }}>
                  <SyutSelect options={ELIGIBILITY_OPTIONS} value={itcEligibility} onChange={v => setItcEligibility(((v as string) || 'ELIGIBLE') as ItcEligibility)} isClearable={false} size="sm" menuPortalTarget={document.body} />
                </div>
              </div>
              {lineTaxCapture ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.5rem', alignItems: 'center', fontSize: '0.85rem' }}>
                  {components.map(c => {
                    const sum = taxByComponent[c.componentCode.toUpperCase()];
                    return (
                      <span key={c.componentCode} style={{ color: 'var(--text-secondary)' }}>
                        {c.componentCode}: <strong>{money(sum?.tax || 0)}</strong>
                        {itcEligibility === 'PARTIAL' && sum && <span style={{ color: 'var(--text-muted)' }}> (ITC {money(sum.eligible)})</span>}
                      </span>
                    );
                  })}
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Summed from the per-line entries; the non-ITC part credits each line's own ledger.</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {taxRows.map(t => (
                    <div key={t.componentCode} style={{ display: 'flex', gap: '0.5rem', alignItems: 'end' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.72rem' }}>{t.componentCode}{t.componentName && t.componentName !== t.componentCode ? ` — ${t.componentName}` : ''}</label>
                        <input className="form-input" style={{ width: 130, textAlign: 'right', opacity: componentDisabled(t.componentCode) ? 0.5 : 1 }} type="number" placeholder="0.00"
                          disabled={!!componentDisabled(t.componentCode)} title={componentDisabled(t.componentCode) || undefined}
                          value={t.taxAmount} onChange={e => updTax(t.componentCode, { taxAmount: e.target.value })} />
                      </div>
                      {itcEligibility === 'PARTIAL' && parseFloat(t.taxAmount) > 0 && (
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: '0.72rem' }}>of which ITC</label>
                          <input className="form-input" style={{ width: 130, textAlign: 'right' }} type="number" placeholder="0.00"
                            value={t.eligibleAmount} onChange={e => updTax(t.componentCode, { eligibleAmount: e.target.value })} />
                        </div>
                      )}
                    </div>
                  ))}
                  {itcEligibility !== 'ELIGIBLE' && taxTotal > 0 && (
                    <div className="form-group" style={{ margin: 0, minWidth: 240 }}>
                      <label className="form-label" style={{ fontSize: '0.72rem' }}>Non-ITC tax credits (cost)</label>
                      <SyutSelect isAsync loadOptions={loadLedgers} defaultOptions cacheOptions={false}
                        seedOptions={costLedgerId ? [{ value: costLedgerId, label: ledgerCache.current.get(costLedgerId) || '' }] : undefined}
                        value={costLedgerId} onChange={v => setCostLedgerId((v as string) || '')} isClearable size="sm" menuPortalTarget={document.body} placeholder={linked ? "Defaults to where the bill costed it" : "Defaults to the first line's ledger"} />
                    </div>
                  )}
                </div>
              )}
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                Enter the tax exactly as printed on the supplier's credit note. The ITC part is handed back in this note's own period — the bill's period is never touched.
              </div>
              {mixedSetNames && <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '0.3rem' }}>These tax components cannot be mixed on one document: {mixedSetNames}.</div>}
              {errors.taxes && <div className="form-error-message">{errors.taxes}</div>}
            </div>
          )}
          {capWarnings.length > 0 && (
            <div className="text-danger" style={{ fontSize: '0.78rem', marginTop: '0.5rem' }}>
              {capWarnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: 240 }}>
              <label className="form-label">Narration</label>
              <textarea className="form-textarea" rows={2} value={narration} onChange={e => setNarration(e.target.value)} />
            </div>
            <div style={{ minWidth: 260, fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Tax reversed (as printed)</span><strong>{money(taxTotal)}</strong></div>
              {taxTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                  <span>ITC handed back / to cost</span><span>{money(eligibleTotal)} / {money(toCost)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderTop: '1px solid var(--border-color)', fontSize: '1rem' }}><span>Reduces payable by</span><strong>{currencyCode} {money(grandTotal)}</strong></div>
              {currencyCode && functionalCurrency && currencyCode !== functionalCurrency && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  <span>≈ {functionalCurrency} @ {exchangeRate || '—'}{linked ? " (the bill's rate)" : ''}</span>
                  <span>{functionalCurrency} {money(grandTotal * (parseFloat(exchangeRate) || 0))}</span>
                </div>
              )}
              {grandTotal > 0 && <>
                {linked && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0', color: 'var(--text-secondary)', fontSize: '0.8rem' }}><span>Settles bill {prefill?.invoiceNumber || ''}</span><span>{money(toBill)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0', color: toAccount > 0.005 ? '#2563eb' : 'var(--text-secondary)', fontSize: '0.8rem' }}><span>On account (set off later)</span><span>{money(toAccount)}</span></div>
              </>}
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || (!!editId && !isDirty)} title={editId && !isDirty ? 'No changes to save' : undefined}><IconSystem.check size={16} /> {editId ? 'Save Changes' : 'Post Debit Note'}</button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen}
        title={editId ? 'Save Changes' : 'Post Debit Note'}
        message={`${editId ? 'Re-post' : 'Post'} this debit note for ${currencyCode} ${money(grandTotal)}? ${settlementSentence}${duplicates.length > 0 ? ` Note: the supplier number ${supplierCreditNoteNumber.trim()} is already recorded on ${duplicates[0].invoiceNumber}.` : ''}`}
        confirmLabel={editId ? 'Save' : 'Post'} cancelLabel="Cancel" variant="default" onConfirm={handleConfirmSave} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={discardConfirmOpen} title="Discard changes?" message="You have unsaved changes on this debit note. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

// ---- View modal ----
const NoteViewModal: React.FC<{ note: PurchaseDebitNoteDto | null; onClose: () => void }> = ({ note, onClose }) => {
  if (!note) return null;
  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">Debit Note {note.debitNoteNumber}</h2><button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button></div>
        <div className="modal-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            <div><div style={{ color: 'var(--text-muted)' }}>Supplier</div><strong>{note.partyName}</strong>{note.supplierCreditNoteNumber ? <div style={{ color: 'var(--text-secondary)' }}>Their credit note: {note.supplierCreditNoteNumber}{note.supplierCreditNoteDate ? ` · ${note.supplierCreditNoteDate.slice(0, 10)}` : ''}</div> : null}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Date</div>{(note.debitNoteDate || '').slice(0, 10)}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Against</div>{note.againstInvoiceId ? <>{note.againstInvoiceNumber}{note.againstSupplierInvoiceNumber ? ` (${note.againstSupplierInvoiceNumber})` : ''}</> : 'No bill — on account'}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Status</div>{statusBadges(note)}</div>
          </div>
          {note.reason && <div style={{ fontSize: '0.82rem', marginBottom: '0.5rem' }}><span style={{ color: 'var(--text-muted)' }}>Reason: </span>{note.reason}</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}><th style={{ padding: '0.3rem', width: 30 }}>#</th><th style={{ padding: '0.3rem' }}>{note.lines.some(l => !!l.itemId) ? 'Ledger (credited) / Item returned' : 'Ledger (credited)'}</th><th style={{ padding: '0.3rem' }}>Description</th><th style={{ padding: '0.3rem', textAlign: 'right', width: 120 }}>Amount</th></tr></thead>
            <tbody>{note.lines.map(l => (
              <tr key={l.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                <td style={{ padding: '0.3rem', verticalAlign: 'top' }}>{l.lineNo}</td>
                <td style={{ padding: '0.3rem', verticalAlign: 'top' }}>
                  {l.itemId ? (
                    <>
                      <ItemLineSummary l={l} stockLabel="Stock out" />
                      {l.sourceInvoiceLineNo != null && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Returns bill line {l.sourceInvoiceLineNo}</div>}
                    </>
                  ) : (
                    <>{l.expenseLedgerName || '—'}{l.sourceInvoiceLineNo != null ? <span style={{ color: 'var(--text-muted)' }}> · bill line {l.sourceInvoiceLineNo}</span> : null}</>
                  )}
                </td>
                <td style={{ padding: '0.3rem', whiteSpace: 'pre-wrap' }}>{l.description || '—'}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(l.lineAmount)}</td>
              </tr>
            ))}</tbody>
          </table>
          {(note.taxes?.length ?? 0) > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              <div className="form-label" style={{ marginBottom: '0.25rem' }}>Tax reversed (as printed{note.taxCaptureLevel === 'LINE' ? ', per line' : ''})</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}><th style={{ padding: '0.3rem' }}>Component</th><th style={{ padding: '0.3rem', textAlign: 'right' }}>Tax</th><th style={{ padding: '0.3rem', textAlign: 'right' }}>ITC handed back</th><th style={{ padding: '0.3rem', textAlign: 'right' }}>To cost</th></tr></thead>
                <tbody>{note.taxes.map(t => (
                  <tr key={t.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '0.3rem' }}>{t.lineNo != null ? `L${t.lineNo} · ` : ''}{t.componentCode}</td>
                    <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(t.taxAmount)}</td>
                    <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(t.eligibleAmount)}</td>
                    <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(t.taxAmount - t.eligibleAmount)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          <div style={{ marginLeft: 'auto', maxWidth: 300, marginTop: '0.75rem', fontSize: '0.85rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Subtotal</span><strong>{money(note.subtotal)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Tax reversed</span><strong>{money(note.taxTotal)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderTop: '1px solid var(--border-color)', fontSize: '1rem' }}><span>Total</span><strong>{note.currencyCode} {money(note.grandTotal)}</strong></div>
            {Math.abs(note.exchangeRate - 1) > 0.000001 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}><span>≈ base @ {note.exchangeRate}</span><span>{money(note.grandTotal * note.exchangeRate)}</span></div>
            )}
            {note.docStatus === 'POSTED' && <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', color: 'var(--text-secondary)' }}><span>Settled the bill</span><span>{money(note.appliedToBill)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', color: 'var(--text-secondary)' }}><span>On account</span><span>{money(note.onAccount)}</span></div>
            </>}
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </>
  );
};

// ---- Screen ----
// The purchase notes register (ADR-0053): OUR debit notes (reduce a bill) and the SUPPLIER's debit
// notes (add to one) together. Ours open in this screen's own modals; a supplier debit note is a
// bill-class document and opens in the bill form/view in debit-note mode.
type NoteViewMode = 'list' | 'grid';
const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];
const NOTE_COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'noteType', label: 'Type' },
  { id: 'noteNumber', label: 'Our No.' },
  { id: 'supplierReference', label: 'Supplier No.' },
  { id: 'noteDate', label: 'Date' },
  { id: 'partyName', label: 'Supplier' },
  { id: 'againstInvoiceNumber', label: 'Bill' },
  { id: 'reason', label: 'Reason', defaultVisible: false },
  { id: 'grandTotal', label: 'Total' },
  { id: 'baseTotal', label: 'Total (base)', defaultVisible: false },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];
const NOTE_TYPE_OPTIONS: SelectOption[] = [
  { value: '', label: 'All notes' },
  { value: 'DEBIT_NOTE', label: 'Our debit notes (reduce a bill)' },
  { value: 'SUPPLIER_DEBIT_NOTE', label: 'Supplier debit notes (add to a bill)' },
];
const isSupplierNote = (r: PurchaseNoteRegisterRowDto) => r.noteType === 'SUPPLIER_DEBIT_NOTE';
const rowBlockReason = (r: PurchaseNoteRegisterRowDto): string | null => {
  if (r.docStatus !== 'POSTED') return r.docStatus === 'CANCELLED' ? 'Cancelled' : 'Not posted';
  if (r.isLocked) return 'Locked (filed or closed period)';
  return null;
};
const supplierNoteBlockReason = (b: PurchaseInvoiceDto): string | null => {
  if (b.docStatus !== 'POSTED') return b.docStatus === 'CANCELLED' ? 'Cancelled' : 'Not posted';
  if (b.isLocked) return b.lockSource || 'Locked (filed or closed period)';
  if ((b.otherSettlements || []).length > 0) return 'Paid or set off';
  return null;
};
const noteTypeBadge = (r: PurchaseNoteRegisterRowDto): React.ReactNode => (
  <span className="badge" style={{ background: isSupplierNote(r) ? '#7c3aed' : '#0d9488', color: '#fff' }}
    title={isSupplierNote(r) ? "The supplier's debit note — adds to what we owe" : 'Our debit note — reduces what we owe'}>
    {isSupplierNote(r) ? 'Supplier' : 'Ours'}
  </span>
);
const rowStatusBadges = (r: PurchaseNoteRegisterRowDto): React.ReactNode => (
  <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap' }}>
    <span className="badge">{r.docStatus}</span>
    {r.docStatus === 'POSTED' && r.onAccount > 0 && <span className="badge" style={{ background: '#2563eb', color: '#fff' }} title="Supplier debit balance — set off against a bill later">On account {money(r.onAccount)}</span>}
    {r.isLocked && <span className="badge" style={{ background: '#6b7280', color: '#fff' }}>🔒 Locked</span>}
  </span>
);

interface PurchaseDebitNoteScreenProps { moduleIcon?: string; tabId?: string; moduleName?: string; }

const PurchaseDebitNoteScreen: React.FC<PurchaseDebitNoteScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('purchase-debit-note');
  // A supplier debit note is a bill-class document: recording, editing and cancelling it are the
  // Purchase Invoice permissions (ADR-0053); seeing it here is this screen's VIEW.
  const canSdn = useCan('purchase-invoice');
  const canSetoff = useCan('supplier-setoff');
  const { openRecord } = useNavigation();
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [queryParams, setQueryParams] = useState<PurchaseNoteRegisterQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = usePurchaseNotesRegister(queryParams);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<NoteViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } = useColumnVisibility('purchase-notes-register-columns', NOTE_COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [noteType, setNoteType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  // Our debit note modals
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editNote, setEditNote] = useState<PurchaseDebitNoteDto | null>(null);
  const [viewNote, setViewNote] = useState<PurchaseDebitNoteDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);
  // Supplier debit note modals (the bill form / view in debit-note mode)
  const [sdnFormOpen, setSdnFormOpen] = useState(false);
  const [sdnFormMode, setSdnFormMode] = useState<'create' | 'edit'>('create');
  const [editSdn, setEditSdn] = useState<PurchaseInvoiceDto | null>(null);
  const [viewSdn, setViewSdn] = useState<PurchaseInvoiceDto | null>(null);
  const [sdnCreateInstanceId, setSdnCreateInstanceId] = useState('1');
  const sdnMinimizeRef = useRef<(() => void) | null>(null);
  const setupGate = useSetupGate('PURCHASING');

  const restoreMinimized = useCallback((id: string | null): boolean => {
    if (!id || !isMinimized(id)) return false;
    const modal = getMinimizedModal(id);
    dismissMinimized(id); modal?.onRestore?.();
    return true;
  }, [isMinimized, getMinimizedModal, dismissMinimized]);

  const handleView = useCallback(async (r: PurchaseNoteRegisterRowDto) => {
    if (isSupplierNote(r)) {
      try { setViewSdn(await purchaseInvoiceService.getById(r.id)); }
      catch { notificationService.toast.error('Could not load the supplier debit note.'); }
      return;
    }
    try { setViewNote(await purchaseDebitNoteService.getById(r.id)); }
    catch { notificationService.toast.error('Could not load the debit note.'); }
  }, []);

  const minimizeOpenForms = useCallback(() => {
    if (formOpen) formModalMinimizeRef.current?.();
    if (sdnFormOpen) sdnMinimizeRef.current?.();
  }, [formOpen, sdnFormOpen]);

  const handleAdd = useCallback(async () => {
    if (!(await setupGate.ensureReady())) return;
    minimizeOpenForms();
    let next = 1;
    if (tabId) while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    setCreateInstanceId(String(next));
    setFormMode('create'); setEditNote(null); setFormOpen(true);
  }, [tabId, isMinimized, setupGate, minimizeOpenForms]);

  const handleAddSupplierNote = useCallback(async () => {
    if (!(await setupGate.ensureReady())) return;
    minimizeOpenForms();
    let next = 1;
    if (tabId) while (isMinimized(`${tabId}-form-modal-create-sdn-${next}`)) next++;
    setSdnCreateInstanceId(String(next));
    setSdnFormMode('create'); setEditSdn(null); setSdnFormOpen(true);
  }, [tabId, isMinimized, setupGate, minimizeOpenForms]);

  const openForEdit = useCallback(async (id: string, fallbackToView: boolean) => {
    try {
      const full = await purchaseDebitNoteService.getById(id);
      const blocked = editBlockReason(full);
      if (blocked) {
        if (fallbackToView) { setViewNote(full); notificationService.toast.info(`Opened read-only — ${blocked}.`); }
        else notificationService.toast.warning(`This debit note cannot be edited — ${blocked}.`);
        return;
      }
      setEditNote(full); setFormMode('edit'); setFormOpen(true);
    } catch { notificationService.toast.error('Could not load the debit note.'); }
  }, []);

  const handleEdit = useCallback(async (r: PurchaseNoteRegisterRowDto) => {
    if (!(await setupGate.ensureReady())) return;
    if (isSupplierNote(r)) {
      if (restoreMinimized(tabId ? `${tabId}-form-modal-edit-sdn-${r.noteNumber || r.id}` : null)) return;
      try {
        const full = await purchaseInvoiceService.getById(r.id);
        const blocked = supplierNoteBlockReason(full);
        if (blocked) { notificationService.toast.warning(`This supplier debit note cannot be edited — ${blocked}.`); return; }
        setEditSdn(full); setSdnFormMode('edit'); setSdnFormOpen(true);
      } catch { notificationService.toast.error('Could not load the supplier debit note.'); }
      return;
    }
    if (restoreMinimized(tabId ? `${tabId}-form-modal-edit-${r.noteNumber || r.id}` : null)) return;
    await openForEdit(r.id, false);
  }, [tabId, restoreMinimized, setupGate, openForEdit]);

  useNavTarget('purchase-debit-note', (t) => {
    if (t.mode === 'view') purchaseDebitNoteService.getById(t.recordId).then(setViewNote).catch(() => notificationService.toast.error('Could not open the debit note.'));
    else openForEdit(t.recordId, true);
  });

  const findRow = useCallback((type: PurchaseNoteRegisterRowDto['noteType'], noteNo?: string) =>
    noteNo ? data.find(r => r.noteType === type && r.noteNumber === noteNo) : undefined, [data]);

  const handleRestoreModal = useCallback((mode: 'create' | 'edit', instanceId?: string, noteNo?: string) => {
    if (mode === 'edit') {
      const row = findRow('DEBIT_NOTE', noteNo);
      if (row) purchaseDebitNoteService.getById(row.id).then(setEditNote).catch(() => {});
      setFormMode('edit');
    } else {
      if (instanceId) setCreateInstanceId(instanceId);
      setFormMode('create');
    }
    setFormOpen(true);
  }, [findRow]);

  const handleRestoreSdnModal = useCallback((mode: 'create' | 'edit' | 'copy', instanceId?: string, noteNo?: string) => {
    if (mode === 'edit') {
      const row = findRow('SUPPLIER_DEBIT_NOTE', noteNo);
      if (row) purchaseInvoiceService.getById(row.id).then(setEditSdn).catch(() => {});
      setSdnFormMode('edit');
    } else {
      if (instanceId) setSdnCreateInstanceId(instanceId);
      setSdnFormMode('create');
    }
    setSdnFormOpen(true);
  }, [findRow]);

  const handleRestoreSdnView = useCallback((noteNo: string) => {
    const row = findRow('SUPPLIER_DEBIT_NOTE', noteNo);
    if (row) purchaseInvoiceService.getById(row.id).then(setViewSdn).catch(() => {});
  }, [findRow]);

  const handleDelete = useCallback(async (r: PurchaseNoteRegisterRowDto) => {
    const label = isSupplierNote(r) ? 'Supplier debit note' : 'Debit note';
    const ok = await confirmDelete({ resourceLabel: r.noteNumber || `this ${label.toLowerCase()}` });
    if (!ok) return;
    try {
      if (isSupplierNote(r)) await purchaseInvoiceService.remove(r.id, r.rowVersion);
      else await purchaseDebitNoteService.remove(r.id, r.rowVersion);
      notificationService.toast.success(`${label} ${r.noteNumber || ''} cancelled.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || `Could not cancel the ${label.toLowerCase()}.`);
      if (err?.response?.status === 409) refetch();
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<PurchaseNoteRegisterRowDto>[] = useMemo(() => [
    { id: 'noteType', header: 'Type', accessorKey: 'noteType', size: 90, cell: ({ row }) => noteTypeBadge(row.original as PurchaseNoteRegisterRowDto) },
    { id: 'noteNumber', header: 'Our No.', accessorKey: 'noteNumber', size: 120, cell: ({ row }) => (row.original as PurchaseNoteRegisterRowDto).noteNumber || '—' },
    { id: 'supplierReference', header: 'Supplier No.', accessorKey: 'supplierReference', size: 130, cell: ({ row }) => (row.original as PurchaseNoteRegisterRowDto).supplierReference || '—' },
    { id: 'noteDate', header: 'Date', accessorKey: 'noteDate', size: 105, cell: ({ row }) => ((row.original as PurchaseNoteRegisterRowDto).noteDate || '').slice(0, 10) },
    { id: 'partyName', header: 'Supplier', accessorKey: 'partyName', size: 220 },
    {
      id: 'againstInvoiceNumber', header: 'Bill', accessorKey: 'againstInvoiceNumber', size: 130,
      cell: ({ row }) => {
        const r = row.original as PurchaseNoteRegisterRowDto;
        return r.againstInvoiceNumber || <span style={{ color: 'var(--text-muted)' }}>{isSupplierNote(r) ? 'Stand-alone' : 'On account'}</span>;
      },
    },
    { id: 'reason', header: 'Reason', accessorKey: 'reason', size: 220, cell: ({ row }) => (row.original as PurchaseNoteRegisterRowDto).reason || '' },
    { id: 'grandTotal', header: 'Total', accessorKey: 'grandTotal', size: 140, align: 'right', cell: ({ row }) => { const r = row.original as PurchaseNoteRegisterRowDto; return <span>{r.currencyCode} {money(r.grandTotal)}</span>; } },
    // Home-currency equivalent at the note's rate.
    { id: 'baseTotal', header: 'Total (base)', accessorKey: 'grandTotal', size: 130, align: 'right', cell: ({ row }) => { const r = row.original as PurchaseNoteRegisterRowDto; return money(r.grandTotal * (r.exchangeRate || 1)); } },
    { id: 'status', header: 'Status', accessorKey: 'docStatus', size: 200, cell: ({ row }) => rowStatusBadges(row.original as PurchaseNoteRegisterRowDto) },
  ], []);

  const rowActions = useCallback((r: PurchaseNoteRegisterRowDto): ActionMenuItem[] => {
    const perm = isSupplierNote(r) ? canSdn : can;
    const items: ActionMenuItem[] = [{ id: 'view', label: 'View', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) }];
    // ADR-0051: an on-account balance is meant to be set off against bills — one click there.
    if (!isSupplierNote(r) && r.docStatus === 'POSTED' && r.onAccount > 0 && canSetoff('CREATE'))
      items.push({ id: 'setoff', label: 'Set off against bills', icon: <IconSystem.check size={18} />, onClick: () => openRecord('supplier-setoff', { recordId: r.id, intent: 'from-debit-note' }) });
    if (rowBlockReason(r) === null) {
      if (perm('UPDATE')) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
      if (perm('DELETE')) items.push({ id: 'delete', label: isSupplierNote(r) ? 'Cancel Supplier Debit Note' : 'Cancel Debit Note', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(r), variant: 'danger' });
    }
    return items;
  }, [can, canSdn, canSetoff, openRecord, handleView, handleEdit, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];
  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(refetch)];
  // One "New" menu — the type is chosen before the form opens, and never changes after.
  const newItems: ActionMenuItem[] = [
    ...(can('CREATE') ? [{ id: 'new-dn', label: 'Debit Note (reduces a bill)', icon: <IconSystem.file size={16} />, onClick: () => { void handleAdd(); } }] : []),
    ...(canSdn('CREATE') ? [{ id: 'new-sdn', label: 'Supplier Debit Note (adds to a bill)', icon: <IconSystem.add size={16} />, onClick: () => { void handleAddSupplierNote(); } }] : []),
  ];
  const applyFilters = () => {
    setQueryParams(p => ({
      ...p, noteType: (noteType || undefined) as PurchaseNoteRegisterQueryParams['noteType'],
      fromDate: fromDate || undefined, toDate: toDate || undefined, includeCancelled: includeCancelled || undefined, page: 1,
    }));
    setFilterOpen(false);
  };
  const clearFilters = () => {
    setNoteType(''); setFromDate(''); setToDate(''); setIncludeCancelled(false);
    setQueryParams(p => ({ page: 1, pageSize: p.pageSize }));
  };
  const appliedCount = [noteType, fromDate, toDate, includeCancelled ? '1' : ''].filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title="Purchase Debit Notes"
      description="Our debit notes reduce a bill; the supplier's debit notes add to one (ADR-0049, ADR-0053)"
      icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search number / supplier / bill / reason...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as NoteViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      customToolbarItems={<>
        <ColumnSelector columns={NOTE_COLUMN_DEFS} visibleColumns={visibleColumns} onApply={applyColumns} onReset={resetColumns} />
        <ToolbarAddMenu id="purchase-notes-new" items={newItems} tooltip="Add new — our debit note or the supplier's" />
      </>}
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Type" options={NOTE_TYPE_OPTIONS} value={noteType} onChange={v => setNoteType((v as string) || '')} isClearable={false} size="sm" menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate} onChange={e => setToDate(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeCancelled} onChange={e => setIncludeCancelled(e.target.checked)} /> Include cancelled
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            frozenLeftColumns={['select', 'noteType']} frozenRightColumns={['actions']}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No debit notes yet — use New.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start' }}>
              {data.map(r => (
                <div key={`${r.noteType}-${r.id}`} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                        {noteTypeBadge(r)} {r.noteNumber || '—'}{r.againstInvoiceNumber ? ` · vs ${r.againstInvoiceNumber}` : ''} · {(r.noteDate || '').slice(0, 10)}
                      </div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.partyName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', gap: '0.5rem', paddingTop: '0.4rem', borderTop: '1px solid var(--border-color)', alignItems: 'center' }}>
                    {rowStatusBadges(r)}
                    <strong>{r.currencyCode} {money(r.grandTotal)}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <SetupGateModal gate={setupGate} />
      <NoteFormModal isOpen={formOpen} mode={formMode} note={editNote}
        onSaved={refetch} onClose={() => { setFormOpen(false); setEditNote(null); setFormMode('create'); }}
        tabId={tabId} moduleName={moduleName || 'Purchase Debit Notes'}
        createInstanceId={createInstanceId}
        onRestoreModal={handleRestoreModal}
        minimizeRef={formModalMinimizeRef} />
      <NoteViewModal note={viewNote} onClose={() => setViewNote(null)} />

      <BillFormModal isOpen={sdnFormOpen} mode={sdnFormMode} invoice={editSdn} documentKind="DEBIT_NOTE"
        onSaved={refetch} onClose={() => { setSdnFormOpen(false); setEditSdn(null); setSdnFormMode('create'); }}
        tabId={tabId} moduleName={moduleName || 'Purchase Debit Notes'}
        createInstanceId={sdnCreateInstanceId}
        onRestoreModal={handleRestoreSdnModal} minimizeRef={sdnMinimizeRef} />
      <BillViewModal invoice={viewSdn} onClose={() => setViewSdn(null)}
        tabId={tabId} moduleName={moduleName || 'Purchase Debit Notes'} onRestoreView={handleRestoreSdnView} />
    </EnterpriseScreenShell>
  );
};

export default PurchaseDebitNoteScreen;
